import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";

function Editor({ item, kind, onClose, onSaved }) {
  const [f, setF] = useState(item || { title: "", slug: "", content: "", excerpt: "", image: "", category: "", status: kind === "blog" ? "draft" : "active", seo: { title: "", description: "" } });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      if (item?.id) await api.put(`/admin/${kind}/${item.id}`, f);
      else await api.post(`/admin/${kind}`, f);
      toast.success("Saved");
      onSaved();
    } catch (err) { toast.error(err.friendlyMessage); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto" data-testid={`${kind}-editor`}>
      <div className="bg-[#FAF8F5] max-w-2xl mx-auto lg:my-8 min-h-screen lg:min-h-0 p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif text-2xl">{item?.id ? "Edit" : "New"} {kind === "blog" ? "Post" : "Page"}</h2>
          <button onClick={onClose} aria-label="Close"><X size={20} /></button>
        </div>
        <form onSubmit={save} className="space-y-4">
          <div><label className="tq-label">Title *</label>
            <input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="content-title" className="tq-input" /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="tq-label">Slug</label>
              <input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} placeholder="auto from title" data-testid="content-slug" className="tq-input" /></div>
            <div><label className="tq-label">Status</label>
              <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="content-status" className="tq-input bg-white">
                {kind === "blog" ? <><option value="draft">Draft</option><option value="published">Published</option></> : <><option value="active">Active</option><option value="hidden">Hidden</option></>}
              </select></div>
          </div>
          {kind === "blog" && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="tq-label">Category</label>
                <input value={f.category || ""} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="Styling, Guides…" className="tq-input" /></div>
              <div><label className="tq-label">Featured Image URL</label>
                <input value={f.image || ""} onChange={(e) => setF({ ...f, image: e.target.value })} className="tq-input" /></div>
            </div>
          )}
          {kind === "blog" && <div><label className="tq-label">Excerpt</label>
            <input value={f.excerpt || ""} onChange={(e) => setF({ ...f, excerpt: e.target.value })} className="tq-input" /></div>}
          <div><label className="tq-label">Content (HTML allowed)</label>
            <textarea rows={10} value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} data-testid="content-body" className="tq-input font-mono text-xs" /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="tq-label">SEO Title</label>
              <input value={f.seo?.title || ""} onChange={(e) => setF({ ...f, seo: { ...f.seo, title: e.target.value } })} className="tq-input" /></div>
            <div><label className="tq-label">Meta Description</label>
              <input value={f.seo?.description || ""} onChange={(e) => setF({ ...f, seo: { ...f.seo, description: e.target.value } })} className="tq-input" /></div>
          </div>
          <button type="submit" disabled={busy} data-testid="content-save" className="tq-btn-dark w-full">{busy ? "Saving…" : "Save"}</button>
        </form>
      </div>
    </div>
  );
}

function ContentList({ kind, title }) {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get(`/admin/${kind}`).then(({ data }) => setItems(data));
  useEffect(() => { load(); }, [kind]);
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/${kind}/${id}`); toast.success("Deleted"); load(); };
  return (
    <div data-testid={`admin-${kind}`}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl">{title}</h1>
        <button onClick={() => setEditing({})} data-testid={`${kind}-create-btn`} className="tq-btn-dark !py-2.5"><Plus size={14} /> New</button>
      </div>
      {!items ? <div className="h-48 bg-white animate-pulse" /> : items.length === 0 ? (
        <div className="bg-white border border-[#E5DEC9] p-12 text-center text-[#6E6D6A]">Nothing here yet.</div>
      ) : (
        <div className="bg-white border border-[#E5DEC9] divide-y divide-[#F4EFE6]">
          {items.map((p) => (
            <div key={p.id} className="p-4 flex items-center gap-4" data-testid={`${kind}-row-${p.slug}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium">{p.title}</p>
                <p className="text-xs text-[#9a938a]">/{kind === "blog" ? "blog" : "pages"}/{p.slug} · <span className={p.status === "active" || p.status === "published" ? "text-[#1A3C34]" : "text-[#C5A059]"}>{p.status}</span></p>
              </div>
              <a href={`/${kind === "blog" ? "blog" : "pages"}/${p.slug}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#C5A059] underline">View</a>
              <button onClick={() => setEditing(p)} aria-label="Edit" data-testid={`${kind}-edit-${p.slug}`} className="p-1.5 hover:text-[#C5A059]"><Pencil size={15} /></button>
              <button onClick={() => del(p.id)} aria-label="Delete" className="p-1.5 hover:text-[#8B1E21]"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
      {editing && <Editor item={editing.id ? editing : null} kind={kind} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

export function AdminPages() { return <ContentList kind="pages" title="Pages (CMS)" />; }
export function AdminBlog() { return <ContentList kind="blog" title="Blog" />; }

export function AdminFaqs() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [a, setA] = useState("");
  const load = () => api.get("/admin/faqs").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    await api.post("/admin/faqs", { question: q, answer: a });
    setQ(""); setA("");
    toast.success("FAQ added");
    load();
  };
  return (
    <div className="mt-10" data-testid="admin-faqs">
      <h2 className="font-serif text-2xl mb-4">Homepage FAQs</h2>
      <form onSubmit={add} className="bg-white border border-[#E5DEC9] p-4 space-y-3 mb-4">
        <input required value={q} onChange={(e) => setQ(e.target.value)} placeholder="Question" data-testid="faq-question" className="tq-input" />
        <textarea required value={a} onChange={(e) => setA(e.target.value)} placeholder="Answer" rows={2} data-testid="faq-answer" className="tq-input" />
        <button className="tq-btn-dark !py-2" data-testid="faq-add-btn">Add FAQ</button>
      </form>
      <div className="space-y-2">
        {(items || []).map((f) => (
          <div key={f.id} className="bg-white border border-[#E5DEC9] p-3 flex items-center justify-between gap-3">
            <p className="text-sm">{f.question}</p>
            <button onClick={async () => { await api.delete(`/admin/faqs/${f.id}`); load(); }} aria-label="Delete FAQ" className="text-[#8B1E21] shrink-0"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
